import { useCallback, useEffect, useMemo, useRef } from 'react'
import { buildVidsrcEmbedUrl } from '@govu/shared'
import { useWriteLibrary } from '../library/useWriteLibrary'
import { useLibraryMovies, useLibrarySeries } from '../library/useLibrary'
import { useVidsrcProgress, type VidsrcProgressEvent } from './useVidsrcProgress'

interface Props {
  tmdbId: number | null
  mediaType: 'movie' | 'tv'
  season?: number
  episode?: number
  onClose: () => void
}

/**
 * Fullscreen iframe player. Loads vsrc.su's embed page — same URL the
 * desktop app loads inside its in-app webview. For TV episodes, passes
 * season + episode so Vidsrc deep-links straight into the right episode.
 *
 * Resume strategy (PWA-only — desktop has CDP for this):
 *   1. URL params (`t=`, `start=`, `currentTime=`, `time=`) on the
 *      embed URL. We append every plausible name; at least one is
 *      likely to be respected by Vidsrc.
 *   2. postMessage `{type: 'seek', currentTime: N}` after the iframe
 *      loads, retried a few times. Vidsrc may or may not honour it.
 *
 * Progress write strategy (PWA-only):
 *   - useVidsrcProgress tracks the latest position locally every 15s
 *   - Flushes to Firestore at most once per 60s
 *   - Force-flushes on player close, tab hide, beforeunload, completion
 */
export function PlayerModal({ tmdbId, mediaType, season, episode, onClose }: Props) {
  const { updateMovie, addMovieToLibrary, setEpisodeWatched, addSeriesToLibrary } =
    useWriteLibrary()
  const { entries: libraryMovies } = useLibraryMovies()
  const { entries: librarySeries } = useLibrarySeries()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  // Stored resume position from cloud, captured at modal open and frozen
  // for the lifetime of this play session. Otherwise the URL would
  // re-render every time we wrote new progress, reloading the iframe
  // mid-playback.
  const initialProgressSeconds = useMemo(() => {
    if (tmdbId == null) return 0
    if (mediaType === 'movie') {
      const entry = libraryMovies.find((m) => m.tmdbId === tmdbId)
      const p = entry?.record.progress
      return typeof p === 'number' && p > 0 ? p : 0
    }
    if (mediaType === 'tv' && season != null && episode != null) {
      const entry = librarySeries.find((s) => s.tmdbId === tmdbId)
      const ep =
        entry?.record.episodes?.[String(season)]?.[String(episode)]
      const p = ep?.progress
      return typeof p === 'number' && p > 0 ? p : 0
    }
    return 0
    // The lookups depend on tmdbId / mediaType / season / episode but
    // intentionally NOT on libraryMovies / librarySeries — re-running
    // when those snapshots tick would force the iframe URL to change
    // mid-watch and bounce the player back to the resume point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tmdbId, mediaType, season, episode])

  useEffect(() => {
    if (tmdbId == null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [tmdbId, onClose])

  // Belt-and-braces seek: the URL params handle most cases, but if
  // Vidsrc ignores them entirely (or strips them in the iframe chain)
  // we also send postMessage seek commands after the iframe loads.
  // Sent in several common shapes since the API isn't documented.
  useEffect(() => {
    if (tmdbId == null || initialProgressSeconds <= 0) return
    let attempts = 0
    const tick = () => {
      attempts++
      if (attempts > 6 || !iframeRef.current?.contentWindow) return
      const time = Math.floor(initialProgressSeconds)
      const messages: unknown[] = [
        { type: 'seek', currentTime: time },
        { type: 'seekTo', time },
        { event: 'seek', currentTime: time },
        { action: 'seek', time },
      ]
      for (const msg of messages) {
        try {
          iframeRef.current.contentWindow.postMessage(msg, '*')
        } catch {
          // Cross-origin restrictions might still block; ignore.
        }
      }
    }
    // Try a few times after the iframe is likely interactive.
    const t1 = setTimeout(tick, 2000)
    const t2 = setTimeout(tick, 5000)
    const t3 = setTimeout(tick, 10000)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
    }
  }, [tmdbId, initialProgressSeconds])

  const handleProgress = useCallback(
    async (event: VidsrcProgressEvent) => {
      if (tmdbId == null) return
      // Movies → write progress + lastWatched on the cloud movie record.
      if (mediaType === 'movie') {
        const inLibrary = libraryMovies.some((m) => m.tmdbId === tmdbId)
        if (!inLibrary) {
          await addMovieToLibrary(tmdbId)
        }
        await updateMovie(tmdbId, {
          progress: Math.round(event.currentTime),
          lastWatched: new Date().toISOString(),
        })
        return
      }

      // Series → write per-episode progress; on completion, mark watched.
      if (mediaType === 'tv' && season != null && episode != null) {
        const inLibrary = librarySeries.some((s) => s.tmdbId === tmdbId)
        if (!inLibrary) {
          await addSeriesToLibrary(tmdbId)
        }
        if (event.completed) {
          await setEpisodeWatched(tmdbId, season, episode, true)
        } else {
          await setEpisodeWatched(tmdbId, season, episode, false)
        }
      }
    },
    [
      tmdbId,
      mediaType,
      season,
      episode,
      libraryMovies,
      librarySeries,
      addMovieToLibrary,
      addSeriesToLibrary,
      updateMovie,
      setEpisodeWatched,
    ]
  )

  useVidsrcProgress({
    enabled: tmdbId != null,
    trackEveryMs: 15000,
    flushEveryMs: 60000,
    onProgress: handleProgress,
  })

  if (tmdbId == null) return null

  const src = buildVidsrcEmbedUrl(tmdbId, mediaType, season, episode, initialProgressSeconds)
  const label =
    mediaType === 'tv' && season != null && episode != null
      ? `Episode S${season}E${episode} via Vidsrc`
      : 'Streaming via Vidsrc'

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-3 text-text-primary">
        <span className="text-sm text-text-tertiary">{label}</span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-3 py-1.5 text-sm bg-bg-tertiary hover:bg-bg-secondary"
        >
          Close
        </button>
      </div>
      {/*
        sandbox is the PWA equivalent of the desktop's
        webContents.setWindowOpenHandler({action:'deny'}) — without
        allow-popups or allow-top-navigation, vsrc's ad layer cannot
        open new tabs or hijack the parent location. allow-same-origin
        is kept so the embed retains its own storage / cookies, and
        allow-presentation keeps fullscreen working alongside the
        existing `allow` attribute and `allowFullScreen`.
      */}
      <iframe
        ref={iframeRef}
        title="Govu player"
        src={src}
        className="h-full w-full flex-1 border-0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-presentation"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        referrerPolicy="no-referrer"
      />
    </div>
  )
}
